/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
export type ThemeType = {
  fonts: {
    primary: string;
    secondary: string;
  };
  fontSizes: {
    small: string;
    medium: string;
    large: string;
  };
  edgeStrokeWidth: {
    default: number;
    selected: number;
  };
  iconSizes: {
    default: string;
    compact: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
    xxxl: string;
  };
  colors: {
    background: string;
    backgroundTertiary: string;
    text: string;
    primary: string;
    secondary: string;
    tooltipBackground: string;
    tooltipText: string;
    headerColor: string;
    headerTextColor: string;
    fileModalHeaderTextColor: string;
    defaultTextColor: string;
    secondaryTextColor: string;
    primaryBlue: string;
    primaryLight: string;
    primaryDark: string;
    primaryElementFontColor: string;
    primaryButtonColor: string;
    buttonPrimaryColor: string;
    buttonPrimaryHoverColor: string;
    buttonSecondaryColor: string;
    buttonSecondaryHoverColor: string;
    linkColor: string;
    selectedTabColor: string;
    helpTextColor: string;
    errorTextColor: string;
    fileNodeSelectedBackgroundColor: string;
    buttonDisabledColor: string;
    selectionBackgroundPrimary: string;
    selectionBackgroundPrimaryDark: string;
    tableSelectActionColor: string;
    dropdownBackground: string;
    dropdownBorderColor: string;
    dropdownText: string;
    dropdownItemHoverBackground: string;
    reactFlowBackground: string;
    themeIconColor: string;
    entryHighlightColor: string;
    tabHoverColor: string;

    // User menu chevron in navbar
    menuButtonColor: string;
    headerControlHighlight: string;

    avatarBackgroundColor: string;

    // Transfer list container colors
    transferListContainerBorderColor: string;

    pageBackgroundColor: string;
    searchBackgroundColor: string;
    searchBorderColor: string;
    searchTextColor: string;
    searchMainColor: string;
    tableBackgroundColor: string;
    tableTextColor: string;

    positive: string;
    negative: string;

    primaryActive: string;
    secondaryActive: string;

    // Nodes
    inputNodeBorderColor: string;
    inputNodeBackgroundColor: string;
    inputNodeHandleOutlineColor: string;
    fileButtonColor: string;
    editButtonHoverBackgroundColor: string;
    editButtonColor: string;
    edgeColor: string;
    edgeSelectedColor: string;

    controlBarBorderColor: string;
    canvasPatternColor: string;

    // QuickForm Validation Colors
    validationWarningBackground: string;
    validationWarningBorder: string;
    validationWarningText: string;
    validationErrorIcon: string;

    collectionAccentColor: string;
    fileTypeBadgeColors: {
      proto: string;
      xml: string;
      unknown: string; // fallback for UNDETECTED and unrecognised types
    };
  };
  dimensions: {
    button: {
      width: string;
      height: string;
    };
    input: {
      height: string;
    };
    dropdownWidth: string;
    tableWidth: string;
    fileTreeIconSize: string;
    commandBarButtonWidth: string;
    iconSizeLarge: string;
    inputNodeBorderRadius: number;
    controlBarButtonSize: number;
  };
};

// Non-color values
const commonTheme = {
  fonts: {
    primary: "'Inter', sans-serif",
    secondary: "'Roboto', sans-serif",
  },
  fontSizes: {
    small: "14px",
    medium: "16px",
    large: "20px",
  },
  edgeStrokeWidth: {
    default: 2,
    selected: 5,
  },
  iconSizes: {
    default: "15px",
    compact: "12px",
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "10px",
    lg: "16px",
    xl: "28px",
    xxl: "30px",
    xxxl: "80px",
  },
  dimensions: {
    button: {
      width: "35px",
      height: "35px",
    },
    input: {
      height: "40px",
    },
    dropdownWidth: "200px",
    tableWidth: "80%",
    fileTreeIconSize: "small",
    commandBarButtonWidth: "188px",
    iconSizeLarge: "24px",
    inputNodeBorderRadius: 5,
    controlBarButtonSize: 22,
  },
};

export const themes: { [key: string]: ThemeType } = {
  light: {
    ...commonTheme,
    colors: {
      // Main layout
      background: "#FFFFFF",
      backgroundTertiary: "#C6CCD0",

      // Primary palette (US Navy branding)
      primary: "#003B4F",
      primaryBlue: "#003B4F",
      primaryLight: "#088199",
      primaryDark: "#08262C",
      primaryElementFontColor: "#FFFFFF",

      // Buttons / accents
      primaryButtonColor: "#276EF1",
      secondary: "#088199",

      buttonPrimaryColor: "#111111",
      buttonPrimaryHoverColor: "#2B2B2B",
      buttonSecondaryColor: "#EFEFF0",
      buttonSecondaryHoverColor: "#E0E1E3",

      // Text
      text: "#0B1E2D",
      defaultTextColor: "#0B1E2D",
      secondaryTextColor: "#33505A",
      linkColor: "#003B4F",
      helpTextColor: "#33505A",
      errorTextColor: "#B30003",

      // Tab hover color
      tabHoverColor: "#e8e8e8a0",

      // File modal header text color
      fileModalHeaderTextColor: "#1a202c",

      // ── User Menu Chevron ───────────────────────────────────────────────────────────────
      menuButtonColor: "#111827bf",
      headerControlHighlight: "rgba(17,24,39,0.08)",

      avatarBackgroundColor: "#111111",

      // Transfer list containers
      transferListContainerBorderColor: "#d7d1d1",

      // Navigation, tooltips, etc.
      headerColor: "#F6F6F6",
      headerTextColor: "#111827",
      tooltipBackground: "#1A1A1A",
      tooltipText: "#FFFFFF",
      selectedTabColor: "#003B4F",

      // Selection highlights
      fileNodeSelectedBackgroundColor: "rgba(62,99,221,0.08)",
      selectionBackgroundPrimary: "rgba(62,99,221,0.08)",
      selectionBackgroundPrimaryDark: "rgba(62,99,221,0.16)",
      tableSelectActionColor: "#276EF1",

      // Disabled button Color
      buttonDisabledColor: "rgba(0,59,79,0.1)",

      // Dropdown styling
      dropdownBackground: "#FFFFFF",
      dropdownBorderColor: "#C6CCD0",
      dropdownText: "#0B1E2D",
      dropdownItemHoverBackground: "#E8E8E8",

      // ReactFlow canvas
      reactFlowBackground: "#F7F9FA",

      themeIconColor: "#0B1E2D",

      pageBackgroundColor: "#ffffff",
      searchBackgroundColor: "#ffffff",
      searchBorderColor: "#cbd5e0",
      searchTextColor: "#1a202c",
      searchMainColor: "#0056b3",
      tableBackgroundColor: "#ffffff",
      tableTextColor: "#1a202c",

      positive: "#38A169",
      negative: "#E53E3E",

      primaryActive: "#2C5282",
      secondaryActive: "#A0AEC0",

      // Nodes
      inputNodeBorderColor: "#454545",
      inputNodeBackgroundColor: "#EEEE0",
      inputNodeHandleOutlineColor: "#3B4CCA",
      fileButtonColor: "#ffffff",
      editButtonHoverBackgroundColor: "#A0AEC0",
      editButtonColor: "#088199",
      edgeColor: "#003B4F", // primary navy — 11.44:1 on canvas
      edgeSelectedColor: "#3B4CCA",
      entryHighlightColor: "rgba(88,140,237,0.55)",

      controlBarBorderColor: "#E5E7EB",
      canvasPatternColor: "#9AA3AB",

      validationWarningBackground: "rgba(237, 137, 54, 0.1)",
      validationWarningBorder: "#ED8936",
      validationWarningText: "#C05621",
      validationErrorIcon: "#C05621",

      collectionAccentColor: "#088199", // primaryLight — teal
      fileTypeBadgeColors: {
        proto: "#003B4F", // navy   — 12.08:1 on white AAA
        xml: "#276749", // forest green — 6.73:1 on white AA+
        unknown: "#5f6b7a", // slate gray   — 5.43:1 on white AA
      },
    },
  },
  dark: {
    ...commonTheme,
    colors: {
      // ── Surfaces ───────────────────────────────────────────────────────────────
      reactFlowBackground: "#1c1d1f", // USWDS gray-cool-90   — canvas
      background: "#2d2e2f", // USWDS gray-cool-80   — sidebar panels
      backgroundTertiary: "#2d2e2f", // USWDS gray-cool-80   — toolbar
      dropdownBackground: "#3d4551", // USWDS gray-cool-70   — sidebar list items / dropdown menus
      pageBackgroundColor: "#2d2e2f",
      searchBackgroundColor: "#3d4551",
      tableBackgroundColor: "#2d2e2f",

      // ── Text ───────────────────────────────────────────────────────────────────
      text: "#dfe1e2", // USWDS gray-cool-10   — 7.38:1 on node bg
      defaultTextColor: "#dfe1e2",
      secondaryTextColor: "#c6cace", // USWDS gray-cool-20   — 5.88:1 on node bg
      headerTextColor: "#dfe1e2",
      fileModalHeaderTextColor: "#dfe1e2",
      tooltipText: "#dfe1e2",
      dropdownText: "#dfe1e2",
      tableTextColor: "#dfe1e2",
      searchTextColor: "#dfe1e2",
      helpTextColor: "#c6cace",

      // Tab hover color
      tabHoverColor: "#c6cace96",

      // ── Accent ─────────────────────────────────────────────────────────────────
      primary: "#58b4ff", // USWDS blue-30v       — accents, icons
      primaryBlue: "#58b4ff",
      primaryLight: "#a1d3ff", // USWDS blue-20v       — hover
      primaryDark: "#0b4778", // USWDS blue-70v       — active
      primaryButtonColor: "#2491ff", // USWDS blue-40v       — button background
      primaryElementFontColor: "#ffffff",
      secondary: "#2491ff", // USWDS blue-40v
      buttonPrimaryColor: "#565c65",
      buttonPrimaryHoverColor: "#71767A",
      buttonSecondaryColor: "#3d4551",
      buttonSecondaryHoverColor: "#454c56",

      // ── Text roles ──────────────────────────────────────────────────────────────
      linkColor: "#58b4ff",
      selectedTabColor: "#2491ff",
      searchMainColor: "#2491ff",
      errorTextColor: "#ff8d7b", // USWDS red-30v        — 6.06:1 on surface AA+

      // ── Chrome ─────────────────────────────────────────────────────────────────
      headerColor: "#2d2e2f",
      tooltipBackground: "#1c1d1f",

      avatarBackgroundColor: "#3d4551",

      // Transfer list containers
      transferListContainerBorderColor: "#3d4551",

      // ── Status ─────────────────────────────────────────────────────────────────
      positive: "#70e17b", // USWDS green-cool-20v — 8.65:1 on surface AAA
      negative: "#ff8d7b", // USWDS red-30v        — 6.06:1 on surface AA+

      // ── Active states ──────────────────────────────────────────────────────────
      primaryActive: "#2491ff",
      secondaryActive: "#565c65", // USWDS gray-cool-60

      // ── Selection backgrounds ──────────────────────────────────────────────────
      fileNodeSelectedBackgroundColor: "rgba(36,145,255,0.15)",
      selectionBackgroundPrimary: "rgba(36,145,255,0.15)",
      selectionBackgroundPrimaryDark: "rgba(36,145,255,0.25)",
      tableSelectActionColor: "#2491ff",
      buttonDisabledColor: "rgba(36,145,255,0.15)",

      // ── Dropdown ───────────────────────────────────────────────────────────────
      dropdownBorderColor: "#565c65", // USWDS gray-cool-60
      dropdownItemHoverBackground: "#3d4551",

      // ── User Menu Chevron ───────────────────────────────────────────────────────────────
      menuButtonColor: "#dfe1e2",
      headerControlHighlight: "rgba(255,255,255,0.14)",

      // ── ReactFlow nodes ────────────────────────────────────────────────────────
      inputNodeBackgroundColor: "#3d4551", // USWDS gray-cool-70 — nodes float above canvas
      inputNodeBorderColor: "#58b4ff", // USWDS blue-30v     — 7.54:1 on canvas, high visibility
      inputNodeHandleOutlineColor: "#dfe1e2",
      fileButtonColor: "#1c1d1f",
      editButtonHoverBackgroundColor: "#3d4551",
      editButtonColor: "#58b4ff",
      entryHighlightColor: "rgba(36,145,255,0.25)",

      // ── Control bar ────────────────────────────────────────────────────────────
      controlBarBorderColor: "#565c65",
      themeIconColor: "#dfe1e2",
      searchBorderColor: "#565c65",

      canvasPatternColor: "#565c65",

      // ── QuickForm validation ───────────────────────────────────────────────────
      validationWarningBackground: "rgba(221,117,51,0.12)",
      validationWarningBorder: "#dd7533", // USWDS orange-40
      validationWarningText: "#f3966d", // USWDS orange-warm-30
      validationErrorIcon: "#f3966d",
      edgeColor: "#a9aeb1", // gray-cool-30 — 8.25:1 on canvas
      edgeSelectedColor: "#2491ff",

      collectionAccentColor: "#58b4ff", // USWDS blue-30v — matches primary
      fileTypeBadgeColors: {
        proto: "#58b4ff", // bright blue  — 6.08:1 on surface AA+
        xml: "#70e17b", // bright green — 8.26:1 on surface AAA
        unknown: "#c6cace", // gray-cool-20 — 8.25:1 on surface AAA
      },
    },
  },
  // Standard reference: MIL-STD-1472H Section 5.17 / Astro UXDS Status System
  colorblindLight: {
    ...commonTheme,
    colors: {
      // ── Surfaces ───────────────────────────────────────────────────────────────
      background: "#F5F5F5",
      backgroundTertiary: "#D9D9D9",

      // ── Text ───────────────────────────────────────────────────────────────────
      text: "#000000",
      defaultTextColor: "#000000",
      secondaryTextColor: "#4F4F4F",
      headerTextColor: "#000000",
      fileModalHeaderTextColor: "#1A202C",
      tooltipText: "#FFFFFF",
      dropdownText: "#111111",
      tableTextColor: "#111111",
      searchTextColor: "#000000",
      errorTextColor: "#a23737", // USWDS red-60 — 6.69:1 on white AA+

      // Tab hover color
      tabHoverColor: "#4f4f4fcd",

      // ── Accent (blue — safe for all colorblindness types) ──────────────────────
      primary: "#0043CE", // IBM Blue 70 — universally visible
      primaryBlue: "#0043CE",
      primaryLight: "#78A9FF",
      primaryDark: "#002D9C",
      primaryButtonColor: "#0043CE",
      primaryElementFontColor: "#FFFFFF",
      secondary: "#8a3ffc", // IBM Purple 70 — orange reserved for DoD Serious tier
      linkColor: "#0062FF",
      selectedTabColor: "#0043CE",
      helpTextColor: "#4F4F4F",
      searchMainColor: "#0043CE",

      buttonPrimaryColor: "#111111",
      buttonPrimaryHoverColor: "#333333",
      buttonSecondaryColor: "#B0B0B0",
      buttonSecondaryHoverColor: "#9E9E9E",

      // ── Chrome ─────────────────────────────────────────────────────────────────
      headerColor: "#BFBFBF",
      tooltipBackground: "#444444",

      avatarBackgroundColor: "#111111",

      // Transfer list containers
      transferListContainerBorderColor: "#d7d1d1",

      // ── Status colors — DoD semantic hierarchy ─────────────────────────────────
      positive: "#007a33", // Astro green-800 — 5.48:1 on white AA (DoD Normal)
      negative: "#a23737", // USWDS red-60    — 6.69:1 on white AA+ (DoD Critical)

      // ── Active states ──────────────────────────────────────────────────────────
      primaryActive: "#002D9C",
      secondaryActive: "#8A8A8A",

      // ── Selection / highlight backgrounds ──────────────────────────────────────
      fileNodeSelectedBackgroundColor: "#D0E2FF",
      selectionBackgroundPrimary: "#D0E2FF",
      selectionBackgroundPrimaryDark: "#A6C8FF",
      tableSelectActionColor: "#0043CE",
      buttonDisabledColor: "#D0E2FF",

      // ── Dropdown ───────────────────────────────────────────────────────────────
      dropdownBackground: "#FFFFFF",
      dropdownBorderColor: "#B0B0B0",
      dropdownItemHoverBackground: "#D3D3D3",

      // ── User Menu Chevron ───────────────────────────────────────────────────────────────
      menuButtonColor: "#111111",
      headerControlHighlight: "rgba(0,0,0,0.18)",

      // ── Pages / search / table ─────────────────────────────────────────────────
      reactFlowBackground: "#E9E9E9",
      themeIconColor: "#111111",
      pageBackgroundColor: "#FFFFFF",
      searchBackgroundColor: "#FFFFFF",
      searchBorderColor: "#CCCCCC",
      tableBackgroundColor: "#FFFFFF",

      // ── ReactFlow nodes ────────────────────────────────────────────────────────
      inputNodeBorderColor: "#7E8AA2",
      inputNodeBackgroundColor: "#F0F2F5",
      inputNodeHandleOutlineColor: "#0043CE",
      fileButtonColor: "#FFFFFF",
      editButtonHoverBackgroundColor: "#B0B0B0",
      editButtonColor: "#8a3ffc", // matches new secondary (IBM Purple 70)
      edgeColor: "#0043CE", // IBM Blue 70 — 6.42:1
      edgeSelectedColor: "#E07B00",
      entryHighlightColor: "rgba(0,67,206,0.35)",

      // ── Control bar ────────────────────────────────────────────────────────────
      controlBarBorderColor: "#000000",

      canvasPatternColor: "#000000",

      // ── Validation — DoD Caution tier (yellow) ─────────────────────────────────
      validationWarningBackground: "rgba(250,216,0,0.10)", // Astro caution
      validationWarningBorder: "#c7ab48", // USWDS gold-30 — amber/gold border
      validationWarningText: "#504332", // USWDS yellow-70 — 9.59:1 on white AA
      validationErrorIcon: "#a23737", // red — error icon is Critical

      collectionAccentColor: "#0043CE", // IBM Blue 70
      fileTypeBadgeColors: {
        proto: "#0043CE", // IBM Blue 70    — 7.15:1 on #F5F5F5 AA+
        xml: "#276749", // forest green   — 6.17:1 on #F5F5F5 AA+
        unknown: "#6B6B6B", // mid gray       — 4.89:1 on #F5F5F5 AA
      },
    },
  },
  // Palette basis: Astro UXDS dark blue system palette
  // Standard reference: MIL-STD-1472H Section 5.17 / Astro UXDS Status System
  colorblindDark: {
    ...commonTheme,
    colors: {
      // ── Surfaces ───────────────────────────────────────────────────────────────
      reactFlowBackground: "#080c11", // Astro darkblue-950   — canvas, deepest layer
      background: "#172635", // Astro darkblue-900   — sidebar panels
      backgroundTertiary: "#172635", // Astro darkblue-900   — toolbar
      dropdownBackground: "#1c3f5e", // Astro darkblue-700   — sidebar items / dropdown menus
      pageBackgroundColor: "#172635",
      searchBackgroundColor: "#1c3f5e",
      tableBackgroundColor: "#172635",

      // ── Text ───────────────────────────────────────────────────────────────────
      text: "#cbdee9", // Astro darkblue-100   — 7.89:1 on node bg AAA
      defaultTextColor: "#cbdee9",
      secondaryTextColor: "#98bdd3", // Astro darkblue-200   — 5.49:1 on node bg AA+
      headerTextColor: "#cbdee9",
      fileModalHeaderTextColor: "#cbdee9",
      tooltipText: "#cbdee9",
      dropdownText: "#cbdee9",
      tableTextColor: "#cbdee9",
      searchTextColor: "#cbdee9",
      helpTextColor: "#98bdd3",
      errorTextColor: "#fd8ba0", // Astro red-cool-30v — 4.88:1 on node bg AA

      // Tab hover color
      tabHoverColor: "#98bdd3",

      // ── Accent / primary interactive ────────────────────────────────────────────
      primary: "#4dacff", // Astro brightblue-500 — 6.34:1 on surface AA+
      primaryBlue: "#4dacff",
      primaryLight: "#92cbff", // Astro brightblue-400 — hover
      primaryDark: "#005a8f", // Astro darkblue-500   — active/pressed
      primaryButtonColor: "#2f7aa7", // Astro darkblue-400   — button bg
      primaryElementFontColor: "#ffffff",
      secondary: "#2DCCFF", // Astro status-standby — bright cyan accent
      buttonPrimaryColor: "#2f7aa7",
      buttonPrimaryHoverColor: "#4dacff",
      buttonSecondaryColor: "#005a8f",
      buttonSecondaryHoverColor: "#0f6c9e",

      linkColor: "#4dacff",
      selectedTabColor: "#4dacff",
      searchMainColor: "#2f7aa7",

      // ── Chrome ─────────────────────────────────────────────────────────────────
      headerColor: "#172635",
      tooltipBackground: "#080c11", // canvas-dark for max tooltip contrast

      avatarBackgroundColor: "#1c3f5e",

      // Transfer list containers
      transferListContainerBorderColor: "#1c3f5e",

      // ── Status colors — exact Astro UXDS values for dark backgrounds ────────────
      positive: "#56F000", // Astro color-status-normal   — 10.15:1 AAA
      negative: "#FF3838", // Astro color-status-critical — 4.30:1 ≥ 3:1 non-text ✓
      // Note: negative is used for icons/indicators (non-text contrast 3:1 req.)
      //       errorTextColor (#fd8ba0 above) is the text variant, meeting AA 4.5:1

      // ── Active states ──────────────────────────────────────────────────────────
      primaryActive: "#2f7aa7",
      secondaryActive: "#004872", // Astro darkblue-600

      // ── Selection / highlight backgrounds ──────────────────────────────────────
      fileNodeSelectedBackgroundColor: "rgba(77,172,255,0.15)",
      selectionBackgroundPrimary: "rgba(77,172,255,0.15)",
      selectionBackgroundPrimaryDark: "rgba(77,172,255,0.25)",
      tableSelectActionColor: "#2f7aa7",
      buttonDisabledColor: "rgba(77,172,255,0.15)",

      // ── Dropdown ───────────────────────────────────────────────────────────────
      dropdownBorderColor: "#004872", // Astro darkblue-600
      dropdownItemHoverBackground: "#1c3f5e",

      // ── User Menu Chevron ───────────────────────────────────────────────────────────────
      menuButtonColor: "#cbdee9",
      headerControlHighlight: "rgba(255,255,255,0.16)",

      // ── ReactFlow nodes ────────────────────────────────────────────────────────
      inputNodeBackgroundColor: "#1c3f5e", // Astro darkblue-700 — nodes float above canvas
      inputNodeBorderColor: "#4dacff", // Astro brightblue-500 — 8.09:1 on canvas, high visibility
      inputNodeHandleOutlineColor: "#cbdee9",
      fileButtonColor: "#080c11",
      editButtonHoverBackgroundColor: "#1c3f5e",
      editButtonColor: "#4dacff",
      edgeColor: "#98bdd3", // Astro darkblue-200 — 7.73:1 on canvas
      edgeSelectedColor: "#2DCCFF",
      entryHighlightColor: "rgba(77,172,255,0.25)",

      // ── Control bar ────────────────────────────────────────────────────────────
      controlBarBorderColor: "#004872",
      themeIconColor: "#cbdee9",
      searchBorderColor: "#004872",

      canvasPatternColor: "#004872",

      // ── Validation — DoD Caution tier (yellow) ─────────────────────────────────
      validationWarningBackground: "rgba(252,232,58,0.12)", // Astro caution #FCE83A tint
      validationWarningBorder: "#FCE83A", // Astro color-status-caution — 12.27:1 AAA
      validationWarningText: "#FCE83A", // legible on dark bg
      validationErrorIcon: "#FF3838", // Astro color-status-critical

      collectionAccentColor: "#4dacff", // Astro brightblue-500
      fileTypeBadgeColors: {
        proto: "#4dacff", // bright blue       — 6.34:1 on surface AA+
        xml: "#a3e4d7", // soft teal-green   — 10.72:1 on surface AAA
        unknown: "#98bdd3", // muted blue-gray   — 7.73:1 on surface AAA
      },
    },
  },
};

export const ThemeNames = {
  LIGHT: "light",
  DARK: "dark",
  COLORBLIND_LIGHT: "colorblindLight",
  COLORBLIND_DARK: "colorblindDark",
} as const;

export type ThemeName = (typeof ThemeNames)[keyof typeof ThemeNames];
